import { SkyButton, SkyMessageBar } from "@skypointcloud/skypoint-ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  RETRYABLE_TITLE,
  copyReference,
  describeFailure,
  reportFailureShown,
  type DescribedFailure,
} from "@/lib/feedback/failure";
import { DONE_LABEL_MS } from "@/lib/feedback/NoticeRegion";
import { CopyIcon } from "@/lib/icons";

/**
 * An error shown beside the control that failed (ux-s10.md §3 rule 2): inside a
 * dialog or drawer that stays open on error, or directly above a save bar.
 *
 * Unlike a page notice, this bar KEEPS `SkyMessageBar`'s own `role=alert`: while a
 * modal is open, everything outside it (the notice region's live lines included)
 * is `aria-hidden`, so the bar is the only element that can announce an in-dialog
 * error. The "one announcing element" rule is about notices, not inline errors.
 */

/** A failure as `useInlineError` holds it: each `show` is a new occurrence. */
export interface InlineFailure extends DescribedFailure {
  occurrence: number;
}

export interface InlineErrorState {
  error: InlineFailure | null;
  /** Describe `cause`, log it (identifiers only) and show it. */
  show: (cause: unknown, fallbackMessage?: string) => void;
  clear: () => void;
}

export function useInlineError(): InlineErrorState {
  const [error, setError] = useState<InlineFailure | null>(null);

  const show = useCallback((cause: unknown, fallbackMessage?: string) => {
    reportFailureShown(cause);
    const failure = describeFailure(cause, fallbackMessage);
    setError((previous) => ({ ...failure, occurrence: (previous?.occurrence ?? 0) + 1 }));
  }, []);

  const clear = useCallback(() => setError(null), []);

  return useMemo(() => ({ error, show, clear }), [error, show, clear]);
}

export function InlineErrorBar({
  error,
  title,
  onDismiss,
  testId = "inline-error",
}: {
  error: DescribedFailure | InlineFailure | null;
  /** What failed ("We could not save this connection"); defaults to "Try again" when retryable. */
  title?: string;
  onDismiss?: () => void;
  testId?: string;
}) {
  if (!error) return null;
  const occurrence = "occurrence" in error ? error.occurrence : 0;

  return (
    <SkyMessageBar
      // A repeat of the same failure is a new element, so its `role=alert` is
      // announced again rather than updating text a screen reader already read.
      key={occurrence}
      // #1110 review: a cancelled step-up sign-in is the person's own choice, not a failure.
      intent={error.cancelled ? "warning" : "error"}
      layout="multiline"
      title={
        error.cancelled ? undefined : (title ?? (error.retryable ? RETRYABLE_TITLE : undefined))
      }
      testId={testId}
      message={
        <span>
          {error.message}
          {error.printedReference ? (
            <>
              {" "}
              Reference id <span className="font-mono">{error.printedReference}</span>.
            </>
          ) : null}
          <FailureDetails details={error.details} testId={`${testId}-details`} />
        </span>
      }
      actions={
        error.reference ? (
          <CopyReferenceButton reference={error.reference} testId={`${testId}-copy-reference`} />
        ) : undefined
      }
      onDismiss={onDismiss}
    />
  );
}

/**
 * The server's per-field reasons under a failure's sentence (B-23, round 9), a secondary list;
 * nothing when there are none.
 */
export function FailureDetails({
  details,
  testId,
}: {
  details: readonly string[] | undefined;
  testId?: string;
}) {
  if (!details || details.length === 0) return null;
  // Spans, not a list: the message bar renders its message inside phrasing content.
  return (
    <span className="mt-1 block text-sm" data-testid={testId}>
      {details.map((line, index) => (
        <span key={`${index}:${line}`} className="block" data-detail="">
          {line}
        </span>
      ))}
    </span>
  );
}

/**
 * "Copy reference", reading "Copied" for 2 s after the copy went through. A
 * refused clipboard leaves the label alone: the id is on screen to be typed.
 */
export function CopyReferenceButton({ reference, testId }: { reference: string; testId?: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const handle = setTimeout(() => setCopied(false), DONE_LABEL_MS);
    return () => clearTimeout(handle);
  }, [copied]);

  return (
    <SkyButton
      appearance="subtle"
      size="small"
      icon={CopyIcon}
      testId={testId}
      onClick={async () => {
        if (await copyReference(reference)) setCopied(true);
      }}
    >
      {copied ? "Copied" : "Copy reference"}
    </SkyButton>
  );
}
